// ============================================
// Modal Manager Module (ES6)
// 통합 모달 관리 시스템
// ============================================

import { closeModelSelector } from './chat/model-selector.js';
import { closeChatOverlay } from './chat/overlay.js';

const BASE_Z_INDEX = 1000;
const stack = [];  // 열린 모달 스택 (순서대로)
const registry = {};  // modalId -> { close: Function, type: string }

let historyInitialized = false;
let historyPushed = false;

// ============================================
// 모달 등록/해제
// ============================================

export function register(modalId, closeFunction, type = 'modal') {
  registry[modalId] = {
    close: closeFunction,
    type: type  // 'modal', 'bottomSheet', 'fullscreen', 'screen', 'panel'
  };
}

export function unregister(modalId) {
  delete registry[modalId];
  const index = stack.indexOf(modalId);
  if (index > -1) {
    stack.splice(index, 1);
  }
}

// ============================================
// 스택 관리
// ============================================

export function push(modalId) {
  const index = stack.indexOf(modalId);
  if (index > -1) {
    stack.splice(index, 1);
  }

  const wasEmpty = stack.length === 0;
  stack.push(modalId);
  updateZIndexes();

  if (wasEmpty && !historyPushed) {
    history.pushState({ isModalState: true }, '');
    historyPushed = true;
  }
}

export function remove(modalId, silent = false) {
  const index = stack.indexOf(modalId);
  if (index > -1) {
    stack.splice(index, 1);
    if (!silent) {
      updateZIndexes();
    }
  }

  if (stack.length === 0 && historyPushed) {
    historyPushed = false;
    history.replaceState({ isAppState: true }, '');
  }
}

// z-index 설정을 건너뛸 모달 목록 (CSS에서 직접 관리)
const SKIP_ZINDEX_MODALS = ['emailLoginModal', 'resetPasswordModal', 'emailVerificationModal'];

export function updateZIndexes() {
  stack.forEach((modalId, index) => {
    // 인증 모달은 CSS z-index 유지 (200000)
    if (SKIP_ZINDEX_MODALS.includes(modalId)) return;

    const modal = document.getElementById(modalId);
    if (modal) {
      modal.style.zIndex = BASE_Z_INDEX + (index * 10);
    }
  });
}

// ============================================
// History API 연동 (뒤로가기 지원)
// ============================================

function initHistory() {
  if (historyInitialized) return;
  historyInitialized = true;

  history.replaceState({ isAppState: true }, '');
  window.addEventListener('popstate', handlePopState);
}

function handlePopState(event) {
  if (stack.length > 0) {
    const topModalId = stack[stack.length - 1];
    const modalInfo = registry[topModalId];

    if (modalInfo && typeof modalInfo.close === 'function') {
      modalInfo.close();
    } else {
      remove(topModalId);
    }

    if (stack.length > 0) {
      history.pushState({ isModalState: true }, '');
      historyPushed = true;
    }
  }
}

// ============================================
// 모달 닫기
// ============================================

export function closeModal(modalId) {
  const modalInfo = registry[modalId];
  if (modalInfo && typeof modalInfo.close === 'function') {
    modalInfo.close();
  }
  remove(modalId);
}

export function closeTop() {
  if (stack.length > 0) {
    const topModalId = stack[stack.length - 1];
    closeModal(topModalId);
    return true;
  }
  return false;
}

export function closeAll() {
  const stackCopy = [...stack];
  stackCopy.reverse().forEach(modalId => {
    const modalInfo = registry[modalId];
    if (modalInfo && typeof modalInfo.close === 'function') {
      modalInfo.close();
    }
  });
  stack.length = 0;

  if (historyPushed) {
    historyPushed = false;
    history.replaceState({ isAppState: true }, '');
  }
}

// ============================================
// 상태 확인
// ============================================

export function hasOpenModals() {
  return stack.length > 0;
}

export function getTopModal() {
  return stack.length > 0 ? stack[stack.length - 1] : null;
}

export function getStack() {
  return [...stack];
}

export function isOpen(modalId) {
  return stack.includes(modalId);
}

export function debug() {
  console.log('Modal Stack:', stack);
  console.log('History Pushed:', historyPushed);
  console.log('Registry:', Object.keys(registry));
}

// ============================================
// Capacitor 하드웨어 뒤로가기 버튼 처리
// ============================================

function initCapacitorBackButton() {
  if (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
    if (Capacitor.Plugins && Capacitor.Plugins.App) {
      Capacitor.Plugins.App.addListener('backButton', ({ canGoBack }) => {
        if (stack.length > 0) {
          const topModalId = stack[stack.length - 1];
          const modalInfo = registry[topModalId];
          if (modalInfo && typeof modalInfo.close === 'function') {
            modalInfo.close();
          } else {
            remove(topModalId);
          }
        } else {
          Capacitor.Plugins.App.exitApp();
        }
      });
      console.log('Capacitor back button handler initialized');
    }
  }
}

// ============================================
// 기본 모달 등록
// ============================================

export function registerDefaultModals() {
  // app.js
  register('contentDetailModal', () => {
    if (typeof closeContentDetailModal === 'function') closeContentDetailModal();
  }, 'fullscreen');

  register('searchModal', () => {
    if (typeof closeSearchModal === 'function') closeSearchModal();
  }, 'fullscreen');

  register('chatActionModal', () => {
    if (typeof closeChatActionModal === 'function') closeChatActionModal();
  }, 'bottomSheet');

  register('worldInfoModal', () => {
    const modal = document.getElementById('worldInfoModal');
    if (modal) modal.style.display = 'none';
    remove('worldInfoModal', true);
  }, 'modal');

  register('detailMenuModal', () => {
    const modal = document.getElementById('detailMenuModal');
    if (modal) modal.style.display = 'none';
    remove('detailMenuModal', true);
  }, 'modal');

  register('chatOverlay', () => {
    closeChatOverlay();
  }, 'screen');

  // mypage.js
  register('carbonFullModal', () => {
    if (typeof closeCarbonModal === 'function') closeCarbonModal();
  }, 'fullscreen');

  register('settingsModal', () => {
    if (typeof closeSettingsModal === 'function') closeSettingsModal();
  }, 'fullscreen');

  register('carbonRechargeModal', () => {
    if (typeof closeCarbonRechargeModal === 'function') closeCarbonRechargeModal();
  }, 'bottomSheet');

  // auth.js
  register('loginModal', () => {
    if (typeof closeLoginModal === 'function') closeLoginModal();
  }, 'modal');

  register('nicknameModal', () => {
    if (typeof closeNicknameModal === 'function') closeNicknameModal();
  }, 'bottomSheet');

  // creator.js
  register('creationTypeModal', () => {
    if (typeof CreatorModule !== 'undefined' && CreatorModule.closeCreationTypeModal) {
      CreatorModule.closeCreationTypeModal();
    }
  }, 'modal');

  register('creatorSearchModal', () => {
    if (typeof CreatorModule !== 'undefined' && CreatorModule.closeSearchModal) {
      CreatorModule.closeSearchModal();
    }
  }, 'fullscreen');

  register('creatorSortModal', () => {
    if (typeof CreatorModule !== 'undefined' && CreatorModule.closeSortModal) {
      CreatorModule.closeSortModal();
    }
  }, 'modal');

  register('creatorStatsModal', () => {
    if (typeof CreatorModule !== 'undefined' && CreatorModule.closeStats) {
      CreatorModule.closeStats();
    }
  }, 'fullscreen');

  register('characterEditorModal', () => {
    if (typeof CreatorModule !== 'undefined' && CreatorModule.closeEditor) {
      CreatorModule.closeEditor();
    }
  }, 'fullscreen');

  register('imageSelectionModal', () => {
    const modal = document.getElementById('imageSelectionModal');
    if (modal) modal.style.display = 'none';
    remove('imageSelectionModal', true);
  }, 'modal');

  register('imageDetailModal', () => {
    if (typeof CreatorModule !== 'undefined' && CreatorModule.closeImageDetail) {
      CreatorModule.closeImageDetail();
    } else {
      const modal = document.getElementById('imageDetailModal');
      if (modal) modal.style.display = 'none';
      remove('imageDetailModal', true);
    }
  }, 'modal');

  register('autofillConfirmModal', () => {
    if (typeof CreatorModule !== 'undefined' && CreatorModule.closeAutofillModal) {
      CreatorModule.closeAutofillModal();
    }
  }, 'fullscreen');

  register('imageGenModal', () => {
    if (typeof CreatorModule !== 'undefined' && CreatorModule.closeImageGenModal) {
      CreatorModule.closeImageGenModal();
    }
  }, 'fullscreen');

  // 에디터 바텀시트들
  register('editorImageActionSheet', () => {
    if (typeof CreatorModule !== 'undefined' && CreatorModule.closeEditorImageSheet) {
      CreatorModule.closeEditorImageSheet();
    } else {
      const sheet = document.getElementById('editorImageActionSheet');
      if (sheet) sheet.classList.remove('open');
      remove('editorImageActionSheet', true);
    }
  }, 'bottomSheet');

  register('libraryBottomSheet', () => {
    if (typeof CreatorModule !== 'undefined' && CreatorModule.closeLibrarySheet) {
      CreatorModule.closeLibrarySheet();
    } else {
      const sheet = document.getElementById('libraryBottomSheet');
      if (sheet) sheet.classList.remove('open');
      remove('libraryBottomSheet', true);
    }
  }, 'bottomSheet');

  register('sourceImageSheet', () => {
    if (typeof CreatorModule !== 'undefined' && CreatorModule.closeSourceImageSheet) {
      CreatorModule.closeSourceImageSheet();
    } else {
      const sheet = document.getElementById('sourceImageSheet');
      if (sheet) sheet.classList.remove('open');
      remove('sourceImageSheet', true);
    }
  }, 'bottomSheet');

  register('libraryImageDetailModal', () => {
    if (typeof CreatorModule !== 'undefined' && CreatorModule.closeLibraryDetail) {
      CreatorModule.closeLibraryDetail();
    } else {
      const modal = document.getElementById('libraryImageDetailModal');
      if (modal) modal.style.display = 'none';
      remove('libraryImageDetailModal', true);
    }
  }, 'modal');

  // settings.js
  register('chatSettingsModal', () => {
    if (typeof closeChatSettingsModal === 'function') closeChatSettingsModal();
  }, 'fullscreen');

  // profile.js
  register('profileFormModal', () => {
    if (typeof closeProfileFormModal === 'function') closeProfileFormModal();
  }, 'modal');

  // memory.js
  register('memoryPanel', () => {
    if (typeof closeMemoryPanel === 'function') closeMemoryPanel();
  }, 'panel');

  // Model Selector
  register('modelSelectorSheet', () => {
    if (typeof closeModelSelector === 'function') closeModelSelector();
  }, 'bottomSheet');
}

// ============================================
// ModalManager 객체 (하위 호환성)
// ============================================

export const ModalManager = {
  register,
  unregister,
  push,
  remove,
  closeModal,
  closeTop,
  closeAll,
  hasOpenModals,
  getTopModal,
  getStack,
  isOpen,
  updateZIndexes,
  debug
};

// ============================================
// 초기화
// ============================================

function init() {
  initHistory();
  initCapacitorBackButton();
  setTimeout(registerDefaultModals, 100);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// ============================================
// 하위 호환성 - window 객체에 노출
// ============================================

window.ModalManager = ModalManager;
